import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AlertTriangle, ChevronDown, Lock, Plus, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useToast } from '@/hooks/use-toast';

interface PackingItem {
  name: string;
  packed: boolean;
  category?: string;
}

interface ForgottenItem {
  name: string;
  count: number;
  category?: string;
}

interface ForgottenItemsReminderProps {
  onAddItem?: (itemName: string) => void;
  currentDestination?: string;
  currentTripType?: string;
}

export function ForgottenItemsReminder({ 
  onAddItem, 
  currentDestination,
  currentTripType 
}: ForgottenItemsReminderProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [forgottenItems, setForgottenItems] = useState<ForgottenItem[]>([]);
  const [similarDestinationTips, setSimilarDestinationTips] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { hasFeature, subscription } = useSubscription();
  const { toast } = useToast();
  
  const isPremium = hasFeature('personalized_experience');
  const hasPersonalization = true; // Will check consent

  useEffect(() => {
    const analyzePackingHistory = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Check if user has personalization consent
        const { data: consent } = await supabase
          .from('user_consents')
          .select('personalization')
          .eq('user_id', user.id)
          .single();

        if (!consent?.personalization || !isPremium) {
          setLoading(false);
          return;
        }

        // Fetch past trips with packing lists
        const { data: pastTrips, error } = await supabase
          .from('trips')
          .select('id, title, destination, destination_country, trip_type, packing_list, end_date')
          .eq('user_id', user.id)
          .lt('end_date', new Date().toISOString().split('T')[0])
          .order('end_date', { ascending: false })
          .limit(15);

        if (error || !pastTrips) {
          setLoading(false);
          return;
        }

        // Analyze forgotten items (items that were not packed)
        const itemCounts: Record<string, { count: number; category?: string }> = {};
        
        for (const trip of pastTrips) {
          if (trip.packing_list && Array.isArray(trip.packing_list)) {
            for (const rawItem of trip.packing_list) {
              const item = rawItem as unknown as PackingItem;
              if (item && !item.packed && item.name) {
                const key = item.name.toLowerCase();
                if (!itemCounts[key]) {
                  itemCounts[key] = { count: 0, category: item.category };
                }
                itemCounts[key].count++;
              }
            }
          }
        }

        // Get items forgotten at least once, sorted by frequency
        const forgotten = Object.entries(itemCounts)
          .filter(([_, data]) => data.count >= 1)
          .sort((a, b) => b[1].count - a[1].count)
          .slice(0, 8)
          .map(([name, data]) => ({
            name: name.charAt(0).toUpperCase() + name.slice(1),
            count: data.count,
            category: data.category
          }));

        setForgottenItems(forgotten);

        // Generate tips based on similar destinations
        const tips: string[] = [];
        
        if (currentDestination) {
          const similarTrip = pastTrips.find(
            (t) => t.destination_country === currentDestination || 
                   t.destination?.toLowerCase().includes(currentDestination.toLowerCase())
          );
          
          if (similarTrip) {
            tips.push(`You visited ${similarTrip.destination} before - check your previous packing list for ideas!`);
          }
        }

        // Trip type specific tips
        if (currentTripType) {
          const tripTypeCounts: Record<string, number> = {};
          pastTrips.forEach(t => {
            if (t.trip_type) {
              tripTypeCounts[t.trip_type] = (tripTypeCounts[t.trip_type] || 0) + 1;
            }
          });

          if (currentTripType === 'beach' && tripTypeCounts['beach']) {
            tips.push("Beach trips: You've done this before - don't forget reef-safe sunscreen!");
          }
          if (currentTripType === 'business' && tripTypeCounts['business']) {
            tips.push("Business trip veteran: Remember backup chargers and wrinkle-free clothes.");
          }
          if (currentTripType === 'adventure' && tripTypeCounts['adventure']) {
            tips.push("Adventure awaits: Based on your history, pack quick-dry clothing!");
          }
        }

        setSimilarDestinationTips(tips);

      } catch (error) {
        console.error('Error analyzing packing history:', error);
      } finally {
        setLoading(false);
      }
    };

    analyzePackingHistory();
  }, [user, isPremium, currentDestination, currentTripType]);

  const handleAddItem = (itemName: string) => {
    if (onAddItem) {
      onAddItem(itemName);
      toast({
        title: "Item Added",
        description: `${itemName} has been added to your packing list.`,
      });
    }
  };

  // Show locked state for non-premium users
  if (!isPremium) {
    return (
      <Card className="border-dashed opacity-75">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-amber-500" />
            You Often Forget
            <Lock className="h-3.5 w-3.5 text-muted-foreground ml-auto" />
          </CardTitle>
          <CardDescription className="text-xs">
            Premium feature: Get personalized reminders based on your packing history
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full text-xs"
            onClick={() => window.location.href = '/pricing'}
          >
            Upgrade to Unlock
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="h-4 w-4 text-amber-500" />
            You Often Forget
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="h-16 flex items-center justify-center">
            <div className="animate-pulse text-sm text-muted-foreground">Analyzing your history...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (forgottenItems.length === 0 && similarDestinationTips.length === 0) {
    return null; // Don't show anything if no data
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/30 dark:bg-amber-950/20">
        <CollapsibleTrigger asChild>
          <CardHeader className="pb-2 cursor-pointer hover:bg-amber-100/50 dark:hover:bg-amber-900/30 transition-colors rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              You Often Forget
              <Badge variant="secondary" className="ml-auto text-xs bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300">
                {forgottenItems.length} items
              </Badge>
              <ChevronDown className={cn(
                "h-4 w-4 transition-transform text-muted-foreground",
                isOpen && "rotate-180"
              )} />
            </CardTitle>
            <CardDescription className="text-xs">
              Based on your past {forgottenItems.length > 0 ? 'trips' : 'travel patterns'}
            </CardDescription>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 space-y-3">
            {/* Forgotten Items List */}
            {forgottenItems.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
                  Items you frequently left behind:
                </p>
                <div className="flex flex-wrap gap-2">
                  {forgottenItems.map((item, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs gap-1 bg-white dark:bg-background hover:bg-amber-100 dark:hover:bg-amber-900/50 border-amber-300 dark:border-amber-700"
                      onClick={() => handleAddItem(item.name)}
                    >
                      <Plus className="h-3 w-3" />
                      {item.name}
                      {item.count > 1 && (
                        <span className="text-[10px] text-muted-foreground">
                          ({item.count}x)
                        </span>
                      )}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Personalized Tips */}
            {similarDestinationTips.length > 0 && (
              <div className="space-y-1.5 pt-2 border-t border-amber-200 dark:border-amber-800">
                <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
                  💡 Personalized Tips
                </p>
                {similarDestinationTips.map((tip, index) => (
                  <p key={index} className="text-xs text-muted-foreground">
                    • {tip}
                  </p>
                ))}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}